import { useMemo } from 'react';
import { type LucideIcon } from 'lucide-react';
import { TabNav, type TabItem } from '@g17eco/ui-modern/components/ui/tab-nav';

/**
 * Per-option shape used by insights view pickers. `Icon` is a Lucide component
 * — kept separate from TabNav's generic `icon: ReactNode` so callers can
 * continue passing the component directly (rather than pre-instantiating it).
 *
 * `iconBg` / `iconColor` are accepted for historical reasons but currently
 * ignored — TabNav's pill variant uses a uniform blue active state. Leave
 * them on existing call sites; they're harmless.
 */
export interface ViewOption<T extends string> {
  key: T;
  label: string;
  Icon: LucideIcon;
  iconBg?: string;
  iconColor?: string;
  comingSoon?: boolean;
}

interface InsightsViewSelectorProps<T extends string> {
  label?: string;
  options: ViewOption<T>[];
  value: T;
  onChange: (value: T) => void;
}

/**
 * Thin wrapper around `TabNav variant='pills'` — preserves the existing
 * `ViewOption` API used across insights dashboards while routing rendering
 * through the canonical tabs component (Radix-backed, keyboard-navigable,
 * consistent "Soon" badge for disabled items).
 */
export const InsightsViewSelector = <T extends string>({
  label,
  options,
  value,
  onChange,
}: InsightsViewSelectorProps<T>) => {
  const tabs = useMemo<TabItem<T>[]>(
    () =>
      options.map((opt) => ({
        key: opt.key,
        label: opt.label,
        icon: <opt.Icon className='tw:size-3.5' />,
        disabled: opt.comingSoon,
      })),
    [options],
  );

  return (
    <div className='tw:flex tw:items-center tw:flex-wrap tw:gap-2'>
      {label && (
        <div className='tw:text-sm tw:font-medium tw:text-muted-foreground tw:mr-1'>
          {label}
        </div>
      )}
      <TabNav variant='pills' activeTab={value} onChange={onChange} tabs={tabs} />
    </div>
  );
};
